// A product offered beside the cart (the cart's upsell, the delivery step's free-delivery row): a small
// product page that slides over — the buy button first, kept at the top while the rest scrolls, then the
// options as the product page offers them (the store extensions' selectors: a size, a color, an outfit's
// parts), then who made it, who sells it and its description. The button adds the line chosen and closes
// the panel; Back, or the cross, closes it unchanged. `placement`: where the add is counted from.
import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline"
import modalStack from "aldoo-ra/Modal/ModalStack.js"
import { ProductSelectionProvider, ExtensionSlot } from "aldoo-ra/Store/extensions"
import Spinner from "../Spinner"
import ProductPurchaseUI from "../ProductPurchaseUI"
import useT from "../../hooks/useT"
import useSiteLanguage from "../../hooks/useSiteLanguage"
import { useCurrency } from "../../store/currency"
import { LoadProduct, PRODUCT_FIELDS } from "../../api"
import { storeBaseLine, PartPrice } from "../../pages/StoreProductPage"
import ProductGallery from "../ProductGallery"

/** A product's pictures as its page lists them: its own picture first, then the others, the trailer second. */
function galleryOf(product) {
  const picture = product.original || product.thumb || product.square
  const screenshots = product.screenshots || []
  const base = picture && !screenshots.includes(picture) ? [picture, ...screenshots] : screenshots
  if (!product.bookTrailer) return base
  const [first, ...rest] = base
  return first ? [first, { type: "video", url: product.bookTrailer }, ...rest] : [{ type: "video", url: product.bookTrailer }]
}

export default function UpsellProductPanel({ product: listed, onClose, onNavigate, placement = "cart_upsell", title = null }) {
  const t = useT()
  const language = useSiteLanguage()
  const { currency } = useCurrency()
  const [product, setProduct] = useState(null)
  const [failed, setFailed] = useState(false)
  const [shown, setShown] = useState(false)
  const [z, setZ] = useState(null)

  // the whole product: the list's card does not carry its options
  useEffect(() => {
    let alive = true
    LoadProduct({ pid: listed.pid, language, currency, fields: PRODUCT_FIELDS })
      .then((loaded) => alive && (loaded && !loaded.error ? setProduct(loaded) : setFailed(true)))
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [listed.pid, language, currency])

  // slides in, over the cart: one more modal on the store's stack, so it is
  // drawn above the cart and Escape closes it, not the cart under it
  useEffect(() => {
    const id = `upsell-panel-${listed.pid}`
    setZ(modalStack.push(id, onClose, true))
    const frame = requestAnimationFrame(() => setShown(true))
    return () => {
      cancelAnimationFrame(frame)
      modalStack.remove(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listed.pid])

  if (typeof document === "undefined") return null
  const shownProduct = product || listed
  const credit = product?.author_display_name || (product?.author || []).map((author) => author?.name).filter(Boolean).join(", ")
  return createPortal(
    <div
      style={{ zIndex: z ?? 1200 }}
      className={`fixed top-0 right-0 h-[100dvh] w-full md:max-w-[460px] bg-paper shadow-2xl flex flex-col transition-transform duration-200 ${shown ? "translate-x-0" : "translate-x-full"}`}
      role="dialog"
      aria-label={shownProduct.name}
      data-testid="upsell-panel"
    >
      <div className="flex items-center gap-2 h-[60px] px-3 bg-dark text-white shrink-0">
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label={t`Back`} data-testid="upsell-panel-back">
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <span className="flex-1 min-w-0 truncate text-[17px] font-bold">{title || t`Choose options`}</span>
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label={t`Close`}>
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!product && !failed && <Spinner className="py-10" />}
        {failed && <div className="px-5 py-5 text-[14px] text-error">{t`This item is no longer available`}</div>}
        {product && (
          // one selection between the options and the buy button
          <ProductSelectionProvider product={product} baseLine={storeBaseLine}>
            {/* the pictures as the product page shows them on a phone: the big one (swiped), its thumbnails */}
            <div className="px-5 pt-4" data-testid="upsell-panel-gallery">
              <ProductGallery images={galleryOf(product)} productName={product.name} product={product} placement={placement} onAdded={onClose} />
            </div>
            {/* the name and the buy button, kept at the top once the pictures scroll away */}
            <div className="sticky top-0 z-[1] bg-paper border-b border-bordered px-5 py-4 flex flex-col gap-3" data-testid="upsell-panel-buy">
              <span className="text-[17px] font-semibold leading-tight">{shownProduct.name}</span>
              <ProductPurchaseUI product={product} fullWidth placement={placement} onAdded={onClose} />
            </div>
            <div className="px-5 py-5 flex flex-col gap-5">
              {/* the choices first: a size, a color… */}
              <ExtensionSlot name="selectors" product={product} renderPrice={(price) => <PartPrice product={product} {...price} />} className="flex flex-col gap-4" />
              {/* who made it, who sells it */}
              {(credit || product.publisher) && (
                <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[14px]" data-testid="upsell-panel-credits">
                  {credit && (
                    <>
                      <dt className="text-text_secondary">{t`Author`}</dt>
                      <dd className="m-0 text-text">{credit}</dd>
                    </>
                  )}
                  {product.publisher && (
                    <>
                      <dt className="text-text_secondary">{t`Merchant`}</dt>
                      <dd className="m-0 text-text">{product.publisher}</dd>
                    </>
                  )}
                </dl>
              )}
              {/* the description, as the product page has it (no scripts run here) */}
              {product.description && <div className="sf-html text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: product.description }} data-testid="upsell-panel-description" />}
            </div>
          </ProductSelectionProvider>
        )}
      </div>
    </div>,
    document.body
  )
}
